"use client";

import { FilterPillsLoadingList } from "@faire/retailer-visitor-shared/app/(app)/retailer/[locale]/(pagesWithModal)/(defaultHeader)/search/loggedInSearchLoadingSkeleton";
import { AppSignUpDrawer } from "@faire/retailer-visitor-shared/components/AppSignUp/AppSignUpDrawer";
import { PriorityRenderer } from "@faire/retailer-visitor-shared/components/PriorityRenderer/PriorityRenderer";
import { decodeMultimodalQueryText } from "@faire/retailer-visitor-shared/features/MultimodalProductSearch/multimodalQuery";
import { InsiderSlimBanner } from "@faire/retailer-visitor-shared/features/SlimBanners/InsiderSlimBanner";
import {
  SignUpElement,
  SignUpSource,
  SignUpType,
} from "@faire/retailer-visitor-shared/lib/popSignUp";
import { useIsSemDiscoverPage } from "@faire/retailer-visitor-shared/lib/useIsSemDiscoverPage";
import { shouldShowRscProductTileLoggedInSearchPage } from "@faire/retailer-visitor-shared/settings/getSettingRscProductTileLoggedInSearchPage";
import { useSettingDiscoverPageImprovements } from "@faire/retailer-visitor-shared/settings/useSettingDiscoverPageImprovements";
import { DiscoverPageFilteredProductGrids } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/DiscoverPageFilteredProductGrids/DiscoverPageFilteredProductGrids";
import { SearchProductsHeader } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageHeader/SearchPageHeader";
import { SearchPageResults } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageResults/SearchPageResults";
import { BottomDrawer } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/BottomDrawer";
import { useSearchPagePerformanceTrackingRefCallback } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPagePerformanceTrackingWrapper";
import { useSearchPagePerformanceTracking } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/useSearchPagePerformanceTracking";
import { useSearchPageSpellCorrectedQuery } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/useSearchPageSpellCorrectedQuery";
import { useSearchPageLoggedOutBrandTileReviews } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/LoggedOutBrandTileReviews/useSearchPageLoggedOutBrandTileReviews";
import { useSearchPageQueryIsLoading } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/Products/useSearchPageQueryIsLoading";
import { useSearchPageIsFromDiscoverURL } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageIsFromDiscoverURL";
import { useSearchPageProductSearchString } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageProductSearchString";
import { getHelmetData } from "@faire/retailer-visitor-shared/views/search-v2/util/getHelmetData";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { throttleIfPossible } from "@faire/web--source/common/globals/throttleIfPossible";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { NoSSR } from "@faire/web--source/common/NoSSR";
import { Column, Flex } from "@faire/web--source/slate/Layout";
import { trackSignupOpenModalClick } from "@faire/web-api--source/events/signup/click/openModal";
import React, {
  useEffect,
  useMemo,
  useState,
  Suspense,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";

export const SearchPageHeaderAndResults = () => {
  const isLoading = useSearchPageQueryIsLoading();
  const spellCorrectedQuery = useSearchPageSpellCorrectedQuery();
  const productSearchString = useSearchPageProductSearchString();
  const isFromDiscoverURL = useSearchPageIsFromDiscoverURL();
  const isRSCProductTilesEnabled = shouldShowRscProductTileLoggedInSearchPage();

  const { strictLocalize } = useStrictLocalization();

  const { reviews } = useSearchPageLoggedOutBrandTileReviews();

  const helmetData = useMemo(() => {
    const finalQuery =
      spellCorrectedQuery === undefined || isLoading
        ? productSearchString
        : spellCorrectedQuery;

    return getHelmetData(
      decodeMultimodalQueryText(finalQuery),
      isFromDiscoverURL,
      strictLocalize,
      reviews
    );
  }, [
    spellCorrectedQuery,
    isLoading,
    productSearchString,
    isFromDiscoverURL,
    strictLocalize,
    reviews,
  ]);

  if (isRSCProductTilesEnabled) {
    return (
      <RSCPerformanceWrapper
        helmetTitle={helmetData.title}
        helmetDescription={helmetData.description}
        helmetHeader={helmetData.header}
        isLoading={isLoading}
      />
    );
  } else {
    return (
      <LegacyPerformanceWrapper
        helmetTitle={helmetData.title}
        helmetDescription={helmetData.description}
        helmetHeader={helmetData.header}
        isLoading={isLoading}
      />
    );
  }
};

const RSCPerformanceWrapper = React.memo(
  ({
    helmetTitle,
    helmetDescription,
    helmetHeader,
    isLoading,
  }: {
    helmetTitle: string;
    helmetDescription: string;
    helmetHeader: string;
    isLoading: boolean;
  }) => {
    const rscSearchPageElementsRef =
      useSearchPagePerformanceTrackingRefCallback();
    return (
      <HeaderAndResults
        helmetTitle={helmetTitle}
        helmetDescription={helmetDescription}
        helmetHeader={helmetHeader}
        isLoading={isLoading}
        searchPageElementsRef={rscSearchPageElementsRef}
      />
    );
  }
);

const LegacyPerformanceWrapper = React.memo(
  ({
    helmetTitle,
    helmetDescription,
    helmetHeader,
    isLoading,
  }: {
    helmetTitle: string;
    helmetDescription: string;
    helmetHeader: string;
    isLoading: boolean;
  }) => {
    const legacySearchPageElementsRef = useSearchPagePerformanceTracking();
    return (
      <HeaderAndResults
        helmetTitle={helmetTitle}
        helmetDescription={helmetDescription}
        helmetHeader={helmetHeader}
        isLoading={isLoading}
        searchPageElementsRef={legacySearchPageElementsRef}
      />
    );
  }
);

const HeaderAndResults = React.memo(
  ({
    helmetTitle,
    helmetDescription,
    helmetHeader,
    isLoading,
    searchPageElementsRef,
  }: {
    helmetTitle: string;
    helmetDescription: string;
    helmetHeader: string;
    isLoading: boolean;
    searchPageElementsRef: React.Ref<HTMLDivElement>;
  }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const location = useLocation();
    const isSemDiscoverPage = useIsSemDiscoverPage();
    const isDiscoverPageImprovements = useSettingDiscoverPageImprovements();

    const helmetData = useMemo(() => {
      return {
        title: helmetTitle,
        description: helmetDescription,
        header: helmetHeader,
      };
    }, [helmetTitle, helmetDescription, helmetHeader]);

    const handleOpenDrawer = useCallback(() => {
      setIsDrawerOpen(true);
      trackSignupOpenModalClick(
        SignUpType.SEM,
        location.pathname + location.search,
        SignUpSource.AUTO_SEM_BOTTOM_BANNER,
        SignUpElement.AUTO_POPUP
      );
    }, [location.pathname, location.search]);

    const handleScroll = useMemo(
      () =>
        throttleIfPossible(() => {
          if (isSemDiscoverPage) {
            const shouldShowDrawer = getShouldShowDrawer();
            if (shouldShowDrawer) {
              if (!isDrawerOpen) {
                handleOpenDrawer();
              }
            } else {
              setIsDrawerOpen(false);
            }
          }
        }, 300),
      [isSemDiscoverPage, isDrawerOpen, handleOpenDrawer]
    );

    useEffect(() => {
      // Only call this when the setting is enabled
      if (isSemDiscoverPage) {
        getWindow()?.addEventListener("scroll", handleScroll, {
          passive: true,
        });
      }

      return () => {
        getWindow()?.removeEventListener("scroll", handleScroll);
      };
    }, [handleScroll, isSemDiscoverPage]);

    return (
      <Column style={{ width: "100%" }}>
        {isDiscoverPageImprovements ? (
          <DiscoverPageFilteredProductGrids />
        ) : null}
        <NoSSR serverRenderFallback={<FilterPillsLoadingList />}>
          <Suspense fallback={<FilterPillsLoadingList />}>
            <SearchProductsHeader helmetData={helmetData} />
          </Suspense>
        </NoSSR>
        <PriorityRenderer>
          <InsiderSlimBanner />
        </PriorityRenderer>
        {isSemDiscoverPage && (
          <BottomDrawer isOpen={isDrawerOpen}>
            <Flex
              align="center"
              justify="center"
              style={{ overflow: "hidden" }}
            >
              <AppSignUpDrawer />
            </Flex>
          </BottomDrawer>
        )}
        <SearchPageResults
          title={helmetData.header}
          searchPageLoadTimingRef={
            isLoading ? undefined : searchPageElementsRef
          }
        />
      </Column>
    );
  }
);

const MINIMUM_SCROLL_THRESHOLD = 500;
const DRAWER_OPEN_SCROLL_RATIO = 0.85;

const getShouldShowDrawer = (): boolean => {
  const window = getWindow();
  const scrollPosition = window?.scrollY ?? 0;
  const scrollHeight = window?.document.body.scrollHeight ?? 0;
  const windowHeight = window?.innerHeight ?? 0;

  if (scrollPosition < MINIMUM_SCROLL_THRESHOLD) {
    return false;
  }

  if (scrollHeight === 0) {
    // Prevent divide by 0
    return false;
  }

  const scrollRatio = (scrollPosition + windowHeight) / scrollHeight;

  return scrollRatio > DRAWER_OPEN_SCROLL_RATIO;
};
